import type { AlgoliaEventItem } from '~/composables/types/algolia'
import type { EventDescription } from '~/composables/types/event'

/**
 * Sanitizes and validates Algolia queryID
 * QueryID must be a 32-character hexadecimal string
 * @param queryId - The queryID to sanitize
 * @returns Sanitized queryID or null if invalid
 */
export const sanitizeAlgoliaQueryId = (
  queryId: string | null | undefined
): string | null => {
  if (!queryId) return null

  // Remove any trailing slashes or whitespace
  const cleaned = queryId.trim().replace(/\/+$/, '')

  // Validate: must be exactly 32 characters and hexadecimal
  const isValid = /^[a-f0-9]{32}$/i.test(cleaned)

  return isValid ? cleaned : null
}

export const parseEventDescriptionToAlgoliaEventItem = (
  item: EventDescription
): Partial<AlgoliaEventItem> => {
  const hashedId = eventGetters.getId(item)
  const eventRating = item?.avg_review ?? null
  const supplierRating = item?.supplier?.data?.avg_review ?? null
  const supplierGoogleRating = item?.supplier?.data?.google_avg_review ?? null
  const supplierGoogleTotalRating =
    item?.supplier?.data?.google_total_reviews ?? null

  return {
    objectID: `EventDescription::${hashedId}`,
    categories: eventGetters.getCategories(item),
    title: eventGetters.getTitle(item),
    default_duration: eventGetters.getDefaultDuration(item),
    default_price: eventGetters.getDefaultPriceObject(item),
    original_price: eventGetters.getOriginalPriceObject(item),
    hashed_id: hashedId,
    images: eventGetters.getGallery(item),
    permalink: eventGetters.getPermalink(item),
    supplier: eventGetters.getSupplierName(item),
    next_event: eventGetters.getNextInstanceInTimestamp(item),
    rating: eventRating !== null ? eventRating : undefined,
    avg_review: eventRating !== null ? eventRating : undefined,
    supplier_rating: supplierRating !== null ? supplierRating : undefined,
    supplier_google_rating:
      supplierGoogleRating !== null && supplierGoogleRating > 0
        ? supplierGoogleRating
        : undefined,
    supplier_google_total_rating:
      supplierGoogleTotalRating !== null && supplierGoogleTotalRating > 0
        ? supplierGoogleTotalRating
        : undefined,
    has_mobile_classes: eventGetters.getHasMobileClasses(item),
    main_image: {
      alt: eventGetters.getFirstImageAlt(item),
      thumb: eventGetters.getFirstThumb(item),
    },
    has_promotions: eventGetters.getHasPromotion(item),
    type: eventGetters.getAttendanceMode(item),
    min_number_of_people: item.min_number_of_people ?? 0,
    max_number_of_people: item.max_number_of_people ?? 0,
    has_shipping_costs: item.has_shipping_costs ?? 0,
    team_event_only: eventGetters.getIsTeamEvent(item),
  }
}

export const transformToAlgoliaEvent = (data: { data: EventDescription[] }) =>
  (data?.data || []).map(parseEventDescriptionToAlgoliaEventItem)
